import { cloneElement, isValidElement, useEffect, useId, useRef, useState,
         type ReactElement } from "react";
import type { IntakeQuestion, ProjectQuestion } from "./api";

/** Every field either form's question may carry, so one editor can draw both. The server's
 *  `validate_questions` decides which belong to which shape and refuses the rest. */
type Loose = Omit<Partial<ProjectQuestion> & Partial<IntakeQuestion>, "kind"> & {
  id: string; kind: string; label: string; hint: string; required: boolean;
};

/** Which form this editor is writing. */
export type QuestionShape = "project" | "intake";

/**
 * Writing the questions a cohort asks.
 *
 * 🔴 THIS REVERSES A DECISION THIS PROJECT MADE DELIBERATELY (2026-09-08, owner's call), and
 * the reversal has a price worth knowing at the point of using it. `f/teams/propose` used to
 * refuse any payload key outside a fixed allowlist — the second half of "no identity reaches
 * the grouping model", enforced in another repository. A question that ASKS for a name sends
 * a name, under whatever key it was given, and no allowlist can catch that. What survives is
 * narrower: the console never attaches an identifier IT holds. The warning below is the rest.
 *
 * ⚠️ IT WARNS AND NEVER REFUSES. A wordlist is easy to evade and certain to reject something
 * legitimate one day, and a control that cries wolf gets switched off — taking the real
 * finding with it.
 *
 * 🔴 AN ID IS NEVER SHOWN AND NEVER TYPED. Answers are stored under it, so the server mints
 * one and a new question can never inherit a retired question's answers. A question here
 * without an id is new; one with an id is being edited, and rewording it keeps every answer.
 *
 * ⚠️ DELETING ONE DOES NOT DELETE ITS ANSWERS, and the page says so. They stop being readable
 * because nothing remains to say what the key meant — which is recoverable by putting the
 * question back, and would not be if they were cleaned up.
 */
export function QuestionEditor<T extends ProjectQuestion | IntakeQuestion>({
  questions: typed, disabled, warnings = [], words = [], shape = "project", onChange,
}: {
  questions: T[];
  disabled: boolean;
  warnings?: { id: string; label: string; word: string }[];
  /** The server's own wordlist, so the editor can evaluate a question that has not been
   *  saved yet without a second copy of it. */
  words?: string[];
  /**
   * 🔴 THE APPLICATION FORM IS THE OTHER SHAPE (2026-10-13). It asks a grid and a scale and
   * marks three answers by ROLE, and it groups nobody — so it has no "grouped on", no axis and
   * no identity warning: asking for somebody's name is the point of an application.
   */
  shape?: QuestionShape;
  onChange: (qs: T[]) => void;
}) {
  const questions = typed as unknown as Loose[];
  const intake = shape === "intake";
  const [open, setOpen] = useState<string>("");
  // ⚠️ DERIVED FROM THE SAME KEY THE ROW USES, so the drawer and the row can never disagree
  // about which question is being edited — a new question has no id and is keyed by index.
  const openIdx = questions.findIndex((q, i) => (q.id || `new-${i}`) === open);
  const warned = new Set(warnings.map((w) => w.id));
  // 🔴 IT COULD NEVER FIRE ON A NEW QUESTION, WHICH IS WHEN IT MATTERS MOST. `warnings` are
  // computed server-side from the SAVED question set and keyed by id; a question somebody has
  // just typed has `id: ""`, so it was warned about only after being saved — by which point
  // the form may already have been open. `words` is the same list the server derives its
  // warnings from, sent with the payload so there is one definition of it and the editor can
  // evaluate text that has not reached the server yet.
  const reads = (q: Loose) =>
    !intake && q.grouping !== false
    && words.some((w) => `${q.label} ${q.hint}`.toLowerCase().includes(w));
  const identity = (q: Loose) => (!intake && (q.id ? warned.has(q.id) : reads(q)));
  const emit = (qs: Loose[]) => onChange(qs as unknown as T[]);

  const edit = (i: number, patch: Partial<Loose>) =>
    emit(questions.map((q, n) => (n === i ? { ...q, ...patch } : q)));
  const move = (i: number, by: number) => {
    const to = i + by;
    if (to < 0 || to >= questions.length) return;
    const next = [...questions];
    [next[i], next[to]] = [next[to]!, next[i]!];
    emit(next);
  };
  const remove = (i: number) => emit(questions.filter((_q, n) => n !== i));
  const add = () => emit([...questions,
    // ⚠️ NO `id`. The server mints it; a client-invented one is indistinguishable from a
    // retired question's, and answers live under it.
    intake
      ? { id: "", kind: "text", label: "", hint: "", required: true, role: "", max: 500 }
      : { id: "", kind: "text", label: "", hint: "", required: true, grouping: true,
          max: 500 }]);

  const KIND: Record<string, string> = {
    text: "text", choice: "one of", link: "link", timezone: "time zone",
    teammates: "classmates", matrix: "grid", scale: "scale",
  };
  const ROLE: Record<string, string> = {
    name: "their name", discord_username: "Discord name · teammates see it",
    discord_user_id: "Discord user ID",
  };

  return (
    <div className="stack">
      <div className="qedit-list">
      {questions.map((q, i) => {
        const key = q.id || `new-${i}`;
        const isOpen = open === key;
        const flagged = identity(q);
        const kind = q.kind === "choice"
          ? `one of ${(q.options ?? []).length}${q.other ? " or typed" : ""}`
          : q.kind === "matrix"
            ? `grid · ${(q.rows ?? []).length} rows × ${(q.levels ?? []).length} levels`
            : q.kind === "scale" ? `scale ${q.lowest ?? 1} to ${q.highest ?? 5}`
              : (KIND[q.kind] ?? q.kind);
        return (
          <div key={key}
               className={`qrow${isOpen ? " editing" : ""}${flagged ? " flagged" : ""}`}>
            {/* ⚠️ DECORATIVE, AND THE ARROWS ARE THE REAL CONTROL. Drag is an addition; the
                `↑ ↓` buttons are the keyboard path and must stay. */}
            <span className="qhandle" title="Drag to reorder" aria-hidden="true">
              <i /><i /><i /><i /><i /><i />
            </span>
            <span className="qidx">{i + 1}</span>
            <div className="qtext">
              {/* 🔴 THE ACTIONS ARE ON THE TITLE LINE. Right-aligned in a wide row they sat
                  645–805px from the question they act on, and the gap swung 160px between
                  rows because it is a function of title length. Here the distance is the
                  chips and the arrows and nothing else. The ragged right edge is the cost and
                  it is deliberate. */}
              <div className="qtitle">
                <span className="lbl">
                  {q.label || <span className="muted">Untitled question</span>}
                </span>
                {/* A kind is not a tier decision, so it gets no colour. */}
                <span className="qchip">{kind}</span>
                {/* 🔴 THE ORDINARY STATE TAKES THE QUIET CHIP AND THE EXCEPTION TAKES AMBER.
                    Marking the default in the loudest colour teaches the eye to skip it. */}
                {/* 🔴 A PICK IS READ BY THE ARRANGEMENT AND BY NO MODEL, so neither "grouped
                    on" nor the amber "not grouped on" is true of it. It says what it is. */}
                {q.kind === "teammates" && (
                  <span className="qchip">up to {q.max_picks ?? 2} picks</span>)}
                {!intake && q.kind !== "teammates" && q.grouping !== false && (
                  <span className="qchip">grouped on</span>)}
                {!intake && q.kind !== "teammates" && !q.grouping && (
                  <span className="qchip warn"
                        title="The grouping model never reads this answer. It also never makes a
published plan read as stale, and it stays editable after teams are out."
                  >not grouped on</span>)}
                {intake && q.role && <span className="qchip">{ROLE[q.role] ?? q.role}</span>}
                {!q.required && <span className="qchip">optional</span>}
                {q.share && <span className="qchip">teammates see it</span>}
                {/* ⚠️ ON THE ROW, NOT ONLY IN THE DRAWER. Which question is the domain
                    decides how the whole cohort is split, and a teacher scanning seven rows
                    should not have to open each one to find out. */}
                {q.axis === "domain" && <span className="qchip">the domain</span>}
                {q.axis === "seniority" && <span className="qchip">the experience</span>}
                <span className="qacts">
                  <button className="qbtn" disabled={disabled || i === 0}
                          aria-label={`Move ${q.label} up`}
                          onClick={() => move(i, -1)}>↑</button>
                  <button className="qbtn" disabled={disabled || i === questions.length - 1}
                          aria-label={`Move ${q.label} down`}
                          onClick={() => move(i, 1)}>↓</button>
                  <button className={`qbtn wide${isOpen ? " on" : ""}`} disabled={disabled}
                          aria-haspopup="dialog"
                          onClick={() => setOpen(isOpen ? "" : key)}>
                    {isOpen ? "Editing" : "Edit"}</button>
                  <button className="qbtn" disabled={disabled}
                          aria-label={`Remove ${q.label}`}
                          onClick={() => remove(i)}>×</button>
                </span>
              </div>
              {q.hint && <span className="qhint">{q.hint}</span>}
            </div>
            {flagged && (
              <div className="qwarn">
                <span className="tag">READS LIKE IDENTITY</span>
                <p className="t" role="status">
                  This question reads like it asks for identity, and it is one the grouping
                  model reads. That used to be impossible; it is your decision now. Turn
                  “The grouping reads this” off to keep it for yourself.
                </p>
                {/* ⚠️ AN ACTION, NOT JUST A SENTENCE — the remedy was two clicks away inside
                    the question. It still WARNS and never refuses. */}
                {!disabled && (
                  <button className="qbtn wide"
                          onClick={() => edit(i, { grouping: false })}>
                    Stop grouping on it</button>)}
              </div>)}
          </div>);
      })}
      </div>
      {/* 🔴 A DRAWER, BECAUSE EDITING USED TO MOVE THE THING YOU WERE EDITING. The editor
          expanded inline, so every question below it slid down as you typed — and the
          two-column layout narrows the list, which makes that worse. The drawer opens over
          the page and the list holds still.
          🔴 `Done` CLOSES IT AND DOES NOT SAVE. There is one save for the whole tab; a second
          button here that looked like one is how two competing saves happened before. */}
      {openIdx >= 0 && questions[openIdx] && (
        <Drawer index={openIdx} total={questions.length} q={questions[openIdx]!}
                intake={intake} disabled={disabled} onClose={() => setOpen("")}
                onRemove={() => { remove(openIdx); setOpen(""); }}
                edit={(patch) => edit(openIdx, patch)} />)}

      <div className="row gap">
        <button className="btn ghost" disabled={disabled} onClick={add}>Add a question</button>
        <span className="small muted">
          Removing a question does not delete answers already given to it — they stop being
          readable, and putting the question back makes them readable again.
        </span>
      </div>
    </div>
  );
}

function Row({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  /* 🔴 THE LABEL WAS BOUND TO NOTHING. Every field in this drawer rendered a bare `<label>`
     beside its control in a sibling div, so a screen reader announced six unlabelled inputs
     and a separate list of words — and clicking a label focused nothing. The hint travels as
     `aria-describedby` for the same reason: it is the sentence that says what the control
     does, and it was reachable only by eye. */
  const id = useId();
  const described = hint ? `${id}-hint` : undefined;
  const ctl = isValidElement(children)
    ? cloneElement(children as ReactElement<{ id?: string; "aria-describedby"?: string }>,
                   { id, "aria-describedby": described })
    : children;
  return (
    <div className="field">
      <div className="field-main">
        <label htmlFor={id}>{label}</label>
        {hint && <p className="small muted" id={described}>{hint}</p>}
      </div>
      <div className="field-ctl">{ctl}</div>
    </div>
  );
}

/**
 * One question, edited over the page.
 *
 * ⚠️ A MODAL: `Esc` closes, focus returns to the row's Edit button, and the panel is labelled
 * by the question it is editing. The handoff is explicit that drag-to-reorder is an ADDITION
 * and the keyboard controls stay — the same rule applies here.
 */
function Drawer({ index, total, q, intake, disabled, onClose, onRemove, edit }: {
  index: number; total: number; q: Loose; intake: boolean; disabled: boolean;
  onClose: () => void; onRemove: () => void;
  edit: (patch: Partial<Loose>) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    ref.current?.querySelector<HTMLElement>("input, textarea, select")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="drawer-veil" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="drawer" role="dialog" aria-modal="true"
           aria-label={`Question ${index + 1}: ${q.label || "untitled"}`} ref={ref}>
        <div className="drawer-head">
          <div>
            <p className="kick">Question {String(index + 1).padStart(2, "0")} of {total}</p>
            <strong>{q.label || "Untitled question"}</strong>
          </div>
          <span className="spacer" />
          <button className="btn ghost sm" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="drawer-body">
        <div className="stack">
          {intake ? <IntakeFields q={q} disabled={disabled} edit={edit} /> : (<>
                          <Row label="Question">
                            <input className="input wide" value={q.label} disabled={disabled}
                                   onChange={(e) => edit({ label: e.target.value })} />
                          </Row>
                          <Row label="Hint" hint="Shown under the question.">
                            <textarea className="ta" rows={2} value={q.hint} disabled={disabled}
                                      onChange={(e) => edit({ hint: e.target.value })} />
                          </Row>
                          <Row label="Kind" hint="A time zone is the one the grouping reads as hours.">
                            <select className="input" value={q.kind} disabled={disabled}
                                    /* ⚠️ THE AXIS GOES WITH THE KIND. Only a `choice` may
                                       carry one and the server refuses it anywhere else — so
                                       leaving a stale `axis` behind would refuse the save by
                                       naming a control that is no longer on the screen. */
                                    onChange={(e) => {
                                      const kind = e.target.value as ProjectQuestion["kind"];
                                      // ⚠️ A PICK NAMES OTHER PEOPLE: the server forces it off
                                      // the model and off a teammate's page, and it is optional
                                      // unless a teacher says otherwise. The draft says so too,
                                      // so what is on screen is what will be saved.
                                      edit(kind === "choice" ? { kind }
                                        : kind === "teammates"
                                          ? { kind, axis: "", grouping: false, share: false,
                                              required: false, max_picks: q.max_picks ?? 2 }
                                          : { kind, axis: "" });
                                    }}>
                              <option value="text">Text</option>
                              <option value="choice">One of a list</option>
                              <option value="link">A link</option>
                              <option value="timezone">Time zone</option>
                              <option value="teammates">Classmates to team up with</option>
                            </select>
                          </Row>
                          {(q.kind === "choice" || q.kind === "timezone") && (
                            <Row label="Choices" hint="One per line. A list cannot be empty.">
                              <textarea className="ta" rows={4} disabled={disabled}
                                        value={(q.options ?? []).join("\n")}
                                        onChange={(e) => edit({
                                          options: e.target.value.split("\n") })} />
                            </Row>)}
                          {q.kind === "choice" && (
                            /* 🔴 ONLY ON A LIST OF CHOICES, AND THE SERVER REFUSES IT
                               ELSEWHERE. Two people picking the same option pick the same
                               string; on a text question the bucket would be whatever each of
                               them typed, and "Payments" and "payments" would be two domains.
                               ⚠️ THE OPTION ORDER IS THE LADDER, which is why the hint says so
                               where the control is rather than in a document nobody opens. */
                            <Row label="The grouping rule this feeds"
                                 hint="Same domain splits the cohort and never crosses.
                                       Experience is read in the order the choices are listed,
                                       least first. One question each per form.">
                              <select className="input" value={q.axis ?? ""} disabled={disabled}
                                      onChange={(e) => edit({
                                        axis: e.target.value as ProjectQuestion["axis"] })}>
                                <option value="">Nothing — read as text</option>
                                <option value="domain">Same domain</option>
                                <option value="seniority">Experience, least to most</option>
                              </select>
                            </Row>)}
                          {q.kind === "text" && (
                            <Row label="Longest answer" hint="Characters. The box stops here.">
                              <input className="input" type="number" min={1} max={4000}
                                     value={q.max ?? 500} disabled={disabled}
                                     onChange={(e) => edit({ max: Number(e.target.value) })} />
                            </Row>)}
                          {q.kind === "teammates" && (
                            <Row label="Most picks"
                                 hint="How many classmates a student may pick, 1 to 5. Two who
                                       pick each other are kept together, even across domains.">
                              <input className="input" type="number" min={1} max={5}
                                     value={q.max_picks ?? 2} disabled={disabled}
                                     onChange={(e) => edit({
                                       max_picks: Number(e.target.value) })} />
                            </Row>)}
                          <Row label="Required">
                            <input type="checkbox" checked={q.required} disabled={disabled}
                                   onChange={(e) => edit({ required: e.target.checked })} />
                          </Row>
                          <Row label="The grouping reads this"
                               hint={q.kind === "teammates"
                                 ? "Never, for picks: they name other people. The arrangement uses them; no model sees them."
                                 : "Off means the answer reaches no model, never makes a published plan read as stale."}>
                            <input type="checkbox" checked={q.grouping}
                                   disabled={disabled || q.kind === "teammates"}
                                   onChange={(e) => edit({ grouping: e.target.checked })} />
                          </Row>
                          <Row label="Teammates see this"
                               hint={q.kind === "teammates"
                                 ? "Never, for picks. Nobody is told who picked them."
                                 : "Off by default. On means this answer appears on a teammate's own page once teams are published."}>
                            <input type="checkbox" checked={Boolean(q.share)}
                                   disabled={disabled || q.kind === "teammates"}
                                   onChange={(e) => edit({ share: e.target.checked })} />
                          </Row>
                  </>)}
        </div>
        </div>
        <div className="drawer-foot">
          <button className="btn ghost sm" disabled={disabled} onClick={onRemove}>
            Remove question</button>
          <span className="spacer" />
          <button className="btn primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}


/**
 * What an application-form question may be (2026-10-13).
 *
 * ⚠️ THE KIND DECIDES WHAT ELSE IS ASKED, and a change of kind takes along only what the new
 * kind can carry — a role on anything but text is refused by the server, so leaving one behind
 * would refuse the save by naming a control no longer on the screen.
 */
function IntakeFields({ q, disabled, edit }: {
  q: Loose; disabled: boolean; edit: (patch: Partial<Loose>) => void;
}) {
  const lines = (v: string) => v.split("\n");
  return (
    <>
      <Row label="Question">
        <input className="input wide" value={q.label} disabled={disabled}
               onChange={(e) => edit({ label: e.target.value })} />
      </Row>
      <Row label="Hint" hint="Shown under the question.">
        <textarea className="ta" rows={2} value={q.hint} disabled={disabled}
                  onChange={(e) => edit({ hint: e.target.value })} />
      </Row>
      <Row label="Kind" hint="A grid asks for one level on every row; a scale for one number.">
        <select className="input" value={q.kind} disabled={disabled}
                onChange={(e) => {
                  const kind = e.target.value as IntakeQuestion["kind"];
                  edit(kind === "matrix"
                    ? { kind, role: "", rows: q.rows ?? [], levels: q.levels ?? [] }
                    : kind === "scale"
                      ? { kind, role: "", lowest: q.lowest ?? 1, highest: q.highest ?? 5 }
                      : kind === "text" ? { kind } : { kind, role: "" });
                }}>
          <option value="text">Text</option>
          <option value="link">A link</option>
          <option value="choice">One of a list</option>
          <option value="timezone">Time zone</option>
          <option value="matrix">Grid: one level per row</option>
          <option value="scale">Scale</option>
        </select>
      </Row>
      {(q.kind === "choice" || q.kind === "timezone") && (
        <Row label="Choices" hint="One per line. A list cannot be empty.">
          <textarea className="ta" rows={4} disabled={disabled}
                    value={(q.options ?? []).join("\n")}
                    onChange={(e) => edit({ options: lines(e.target.value) })} />
        </Row>)}
      {q.kind === "choice" && (
        <Row label="Also take a typed answer"
             hint="Adds Other… for somebody whose answer is not on the list.">
          <input type="checkbox" checked={Boolean(q.other)} disabled={disabled}
                 onChange={(e) => edit({ other: e.target.checked })} />
        </Row>)}
      {q.kind === "matrix" && (<>
        <Row label="Rows" hint="One per line — each is answered on its own.">
          <textarea className="ta" rows={4} disabled={disabled}
                    value={(q.rows ?? []).join("\n")}
                    onChange={(e) => edit({ rows: lines(e.target.value) })} />
        </Row>
        <Row label="Levels" hint="The same for every row, lowest first. At least two.">
          <textarea className="ta" rows={5} disabled={disabled}
                    value={(q.levels ?? []).join("\n")}
                    onChange={(e) => edit({ levels: lines(e.target.value) })} />
        </Row>
      </>)}
      {q.kind === "scale" && (<>
        <Row label="From" hint="0 or 1.">
          <input className="input" type="number" min={0} max={1} disabled={disabled}
                 value={q.lowest ?? 1}
                 onChange={(e) => edit({ lowest: Number(e.target.value) })} />
        </Row>
        <Row label="To" hint="Up to 10.">
          <input className="input" type="number" min={2} max={10} disabled={disabled}
                 value={q.highest ?? 5}
                 onChange={(e) => edit({ highest: Number(e.target.value) })} />
        </Row>
        <Row label="The lowest means" hint="Optional. Read out beside the number.">
          <input className="input wide" value={q.low ?? ""} disabled={disabled}
                 onChange={(e) => edit({ low: e.target.value })} />
        </Row>
        <Row label="The highest means" hint="Optional.">
          <input className="input wide" value={q.high ?? ""} disabled={disabled}
                 onChange={(e) => edit({ high: e.target.value })} />
        </Row>
      </>)}
      {q.kind === "text" && (<>
        <Row label="Longest answer" hint="Characters. The box stops here.">
          <input className="input" type="number" min={1} max={4000}
                 value={q.max ?? 500} disabled={disabled}
                 onChange={(e) => edit({ max: Number(e.target.value) })} />
        </Row>
        <Row label="What this answer is"
             hint="Only the name, the Discord name and the Discord ID have a role, once each.
                   The console reads them by role, so rewording the question never breaks
                   what teammates see.">
          <select className="input wide" value={q.role ?? ""} disabled={disabled}
                  onChange={(e) => edit({ role: e.target.value as IntakeQuestion["role"] })}>
            <option value="">Nothing special</option>
            <option value="name">The person&apos;s name</option>
            <option value="discord_username">Their Discord name (teammates see it)</option>
            <option value="discord_user_id">Their Discord user ID</option>
          </select>
        </Row>
      </>)}
      <Row label="Required">
        <input type="checkbox" checked={q.required} disabled={disabled}
               onChange={(e) => edit({ required: e.target.checked })} />
      </Row>
    </>
  );
}
